// =============================================================================
// room-counts — "is this location single-room?" predicate
// =============================================================================
// When a venue has only one room, the room's name is the same idea as the
// venue's name and showing it everywhere just adds noise (e.g. "Heuvel1"
// repeated for every slot). This lib fetches room counts per location once
// per session and exposes a useSingleRoomLocations() hook returning the set
// of location IDs that have exactly one room.
//
// Source: booking.v_room_capacity (granted to authenticated by 0001).
// =============================================================================
import { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';

export interface RoomTopology {
  // Set of location IDs whose room_count === 1.
  singleRoomLocations: Set<string>;
  // Map of room_id → location_id. Lets components with just a room_id check
  // single-roomness without an extra lookup.
  roomToLocation: Map<string, string>;
}

const EMPTY: RoomTopology = {
  singleRoomLocations: new Set(),
  roomToLocation: new Map(),
};

let cached: RoomTopology | null = null;
let inflight: Promise<RoomTopology> | null = null;
const subscribers = new Set<(t: RoomTopology) => void>();

async function fetchTopology(): Promise<RoomTopology> {
  const { data, error } = await supabase
    .schema('booking')
    .from('v_room_capacity')
    .select('id, location_id');
  if (error) throw error;

  const rows = (data ?? []) as { id: string; location_id: string }[];
  const counts = new Map<string, number>();
  const roomToLocation = new Map<string, string>();
  for (const r of rows) {
    counts.set(r.location_id, (counts.get(r.location_id) ?? 0) + 1);
    roomToLocation.set(r.id, r.location_id);
  }
  const singleRoomLocations = new Set<string>();
  for (const [locId, n] of counts) {
    if (n === 1) singleRoomLocations.add(locId);
  }
  return { singleRoomLocations, roomToLocation };
}

export function loadRoomTopology(): Promise<RoomTopology> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = fetchTopology()
    .then((t) => {
      cached = t;
      inflight = null;
      for (const cb of subscribers) cb(t);
      return t;
    })
    .catch((e) => {
      inflight = null;
      throw e;
    });
  return inflight;
}

function useRoomTopology(): RoomTopology {
  const [t, setT] = useState<RoomTopology>(() => cached ?? EMPTY);

  useEffect(() => {
    if (cached) {
      setT(cached);
      return;
    }
    let cancelled = false;
    const cb = (next: RoomTopology) => {
      if (!cancelled) setT(next);
    };
    subscribers.add(cb);
    void loadRoomTopology().catch(() => {
      // Silent — components fall back to showing room names.
    });
    return () => {
      cancelled = true;
      subscribers.delete(cb);
    };
  }, []);

  return t;
}

// Returns the set of location IDs that have exactly one room.
export function useSingleRoomLocations(): Set<string> {
  return useRoomTopology().singleRoomLocations;
}

// Hook for components that have a room_id but not a location_id (e.g. the
// booking-detail slots list). Returns true when the room's location has a
// single room and the room name should be hidden.
export function useIsSingleRoomByRoomId(): (roomId: string) => boolean {
  const { singleRoomLocations, roomToLocation } = useRoomTopology();
  return (roomId: string) => {
    const locId = roomToLocation.get(roomId);
    return locId != null && singleRoomLocations.has(locId);
  };
}

// Returns a function that maps a room_id to its location_id (or null if the
// topology hasn't loaded yet or the room is unknown).
export function useRoomToLocation(): (roomId: string) => string | null {
  const { roomToLocation } = useRoomTopology();
  return (roomId: string) => roomToLocation.get(roomId) ?? null;
}

// Returns the list of room_ids belonging to a given location_id.
// Returns null when locationId is null (no filter) so callers can pass the
// result straight through to a "filter when non-null" query helper.
// Returns an empty array when the location has no rooms or the topology
// hasn't loaded yet — callers should treat empty as "no matches".
//
// Memoised so callers can safely depend on the array reference in
// useEffect / useCallback. Without this, every render produced a new array
// → unstable hook deps → infinite re-fetch loop in TodayScreen.
export function useRoomsAtLocation(locationId: string | null): string[] | null {
  const { roomToLocation } = useRoomTopology();
  return useMemo(() => {
    if (!locationId) return null;
    const out: string[] = [];
    for (const [roomId, locId] of roomToLocation) {
      if (locId === locationId) out.push(roomId);
    }
    return out;
  }, [locationId, roomToLocation]);
}
